import { STAT_IDS } from '../hb/types';
import {
  ACCOUNT_SCOPE_ARG,
  factSpec,
  namespaceIdOf,
  STAT_BUILT_IN_ROLES,
  STAT_FACTS,
} from '../story/facts';
import { COND_OPS, type CondDef } from '../story/types';

/** `var.*` id → (type, allowed values), or undefined when the chain has no such var or tell. */
export type VarType = (id: string) => { type: string; values: string[] } | undefined;

/** A problem at a dotted path under the condition (`all.1.op`), with the server's message (without the final period). */
export type CondReport = (path: string, message: string) => void;

const isCombinator = (c: CondDef) => !!(c.all?.length || c.any?.length || c.not);
export const tryInt = (v: string | null | undefined) => /^[-+]?\d+$/.test((v ?? '').trim());
const isBool = (v: string) => v === 'true' || v === 'false';

/**
 * A port of shared-cs `StoryConditions.Validate` (S1-ENG): known facts, argument counts, ops that fit the value type, values that parse,
 * and the places a fact may be read. Messages are `where: …` without the trailing period (the beat validator adds it, like the C#).
 */
export function checkCond(
  c: CondDef | null | undefined,
  where: string,
  varType: VarType,
  roles: Set<string>,
  allowRaid: boolean,
  allowPath: boolean,
  report: CondReport,
  base = '',
) {
  if (c == null) return;
  const at = (p: string) => (base ? (p ? `${base}.${p}` : base) : p);
  if (isCombinator(c)) {
    if (c.fact)
      report(
        at('fact'),
        `${where}: a condition is either a combinator (all, any, not) or a fact, not both`,
      );
    (c.all ?? []).forEach((x, i) =>
      checkCond(x, where, varType, roles, allowRaid, allowPath, report, at(`all.${i}`)),
    );
    (c.any ?? []).forEach((x, i) =>
      checkCond(x, where, varType, roles, allowRaid, allowPath, report, at(`any.${i}`)),
    );
    checkCond(c.not, where, varType, roles, allowRaid, allowPath, report, at('not'));
    return;
  }
  const fact = c.fact ?? '';
  if (!fact) {
    report(at(''), `${where}: empty condition`);
    return;
  }
  const spec = factSpec(fact);
  if (!spec) {
    report(at('fact'), `${where}: unknown fact '${fact}'`);
    return;
  }
  if (spec.where === 'raid' && !allowRaid)
    report(at('fact'), `${where}: raid fact '${fact}' can't be read here (only at a choice)`);
  if (spec.where === 'path' && !allowPath)
    report(at('fact'), `${where}: path fact '${fact}' can't be read here`);

  const args = c.args ?? [];
  const argc = args.length;
  if (argc < spec.minArgs || argc > spec.maxArgs)
    report(
      at('args'),
      `${where}: '${fact}' takes ${spec.minArgs === spec.maxArgs ? spec.minArgs : `${spec.minArgs}..${spec.maxArgs}`} arguments`,
    );
  if (
    namespaceIdOf(fact) === 'ledger' &&
    argc > spec.minArgs &&
    args[argc - 1] !== ACCOUNT_SCOPE_ARG &&
    fact !== 'ledger.outcome'
  )
    report(
      at('args'),
      `${where}: '${fact}' takes '${ACCOUNT_SCOPE_ARG}' as its optional last argument`,
    );

  let type: string = spec.type;
  let allowed: string[] | undefined;
  if (spec.where === 'ticket') {
    const vt = varType(fact.slice(4));
    if (!vt) {
      report(at('fact'), `${where}: unknown hidden variable or tell '${fact}'`);
      return;
    }
    type = vt.type === 'enum' ? 'string' : vt.type;
    allowed = vt.type === 'enum' ? vt.values : undefined;
  }
  if (namespaceIdOf(fact) === 'cast') {
    const role = fact.split('.')[1] ?? '';
    if (!roles.has(role))
      report(at('fact'), `${where}: '${fact}' names a role the chain doesn't cast`);
  }
  if ((STAT_FACTS as readonly string[]).includes(fact) && argc === spec.maxArgs) {
    const who = args[0] ?? '';
    if (
      fact === 'member.stat' &&
      !roles.has(who) &&
      !(STAT_BUILT_IN_ROLES as readonly string[]).includes(who)
    )
      report(at('args'), `${where}: '${fact}' names role '${who}' the chain doesn't cast`);
    const stat = args[argc - 1] ?? '';
    if (!(STAT_IDS as readonly string[]).includes(stat))
      report(
        at('args'),
        `${where}: '${fact}' reads unknown stat '${stat}' (str, end, ref, wit, nrv or cha)`,
      );
  }
  const op = c.op ?? '';
  if (!(COND_OPS as readonly string[]).includes(op)) {
    report(at('op'), `${where}: unknown op '${op}' on '${fact}'`);
    return;
  }
  if ((op === 'gte' || op === 'lte') && type !== 'int')
    report(at('op'), `${where}: '${op}' needs an int fact, and '${fact}' is ${type}`);
  const values = op === 'in' ? (c.values ?? []) : [c.value ?? ''];
  if (op === 'in' && values.length === 0)
    report(at('values'), `${where}: 'in' on '${fact}' needs values`);
  const vpath = op === 'in' ? 'values' : 'value';
  for (const v of values) {
    if (type === 'int' && !tryInt(v))
      report(at(vpath), `${where}: '${fact}' compares an int with '${v}'`);
    else if (type === 'bool' && !isBool(v))
      report(at(vpath), `${where}: '${fact}' compares a bool with '${v}'`);
    else if (allowed && !allowed.includes(v))
      report(at(vpath), `${where}: '${v}' isn't a value of '${fact}'`);
  }
}

/** `StoryConditions.ReadsStat`: whether any leaf reads a stat fact (a stat-gated option or case). */
export function readsStat(c: CondDef | null | undefined): boolean {
  if (c == null) return false;
  if (!isCombinator(c)) return (STAT_FACTS as readonly string[]).includes(c.fact ?? '');
  return (c.all ?? []).some(readsStat) || (c.any ?? []).some(readsStat) || readsStat(c.not);
}

/** Whether any leaf reads a raid fact (so the roller stores `u` instead of an outcome). */
export function readsRaidFact(c: CondDef | null | undefined): boolean {
  if (c == null) return false;
  if (!isCombinator(c)) return factSpec(c.fact)?.where === 'raid';
  return (
    (c.all ?? []).some(readsRaidFact) || (c.any ?? []).some(readsRaidFact) || readsRaidFact(c.not)
  );
}

/** A short one-line form for outlines: `var.companion.bitten = true`, `all(…, …)`. */
export function condText(c: CondDef | null | undefined): string {
  if (c == null) return 'always';
  if (c.all?.length) return `all(${c.all.map(condText).join(', ')})`;
  if (c.any?.length) return `any(${c.any.map(condText).join(', ')})`;
  if (c.not) return `not(${condText(c.not)})`;
  const ops: Record<string, string> = { eq: '=', ne: '≠', gte: '≥', lte: '≤', in: 'in' };
  const args = (c.args ?? []).length ? `(${(c.args ?? []).join(', ')})` : '';
  const value = c.op === 'in' ? `[${(c.values ?? []).join(', ')}]` : (c.value ?? '');
  return `${c.fact ?? '?'}${args} ${ops[c.op ?? ''] ?? c.op ?? '?'} ${value}`.trim();
}
